// File: src/components/miscellaneous/TermsUpdateModal.js
import React, { useState, useRef } from "react";
import { getFirestore, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useProfile } from "../../context/ProfileContext";
import toast from "react-hot-toast";
import TermsOfService202611 from "./TermsOfService202611";

const TARGET_TERMS_VERSION = "2.3-NOVEMBER-2026";

const TermsUpdateModal = () => {
  const { user, userProfile, refreshContext, isDarkMode } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);
  const db = getFirestore();

  // Condition check:
  // 1. Must be logged in and not an anonymous guest
  // 2. Must already be an onboarded user (new users are handled by OnboardingPage)
  // 3. Has not yet accepted terms version 2.3
  const isLegacyUserMissingTerms =
    user &&
    !user.isAnonymous &&
    userProfile?.isOnboarded === true &&
    userProfile?.legalAgreementVersion !== TARGET_TERMS_VERSION;

  if (!isLegacyUserMissingTerms) {
    return null;
  }

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 24) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!user?.uid) return;
    setIsSubmitting(true);

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        legalAgreementVersion: TARGET_TERMS_VERSION,
        lastTermsUpdateTimestamp: serverTimestamp()
      });

      toast.success("Terms accepted. Welcome back!");
      if (refreshContext) {
        await refreshContext();
      }
    } catch (error) {
      console.error("Failed to commit updated terms agreement:", error);
      toast.error("Failed to update terms status. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto ${isDarkMode ? 'dark' : ''}`}>
      <div 
        className={`w-full max-w-xl p-6 sm:p-8 rounded-3xl shadow-2xl border transition-all my-auto space-y-6 ${
          isDarkMode 
            ? "bg-[#121212] border-zinc-800 text-white" 
            : "bg-white border-slate-200 text-slate-900"
        }`}
      >
        {/* Header */}
        <div className="text-center space-y-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-[#ff00cc]">
            Platform Policy Update
          </span>
          <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight">
            Terms of Service (v2.3)
          </h2>
          <p className={`text-xs sm:text-sm font-medium leading-relaxed max-w-md mx-auto ${
            isDarkMode ? "text-zinc-300" : "text-slate-600"
          }`}>
            We&apos;ve updated our Terms of Service to streamline platform operations and clarify direct promotional licensing. By clicking &apos;Accept &amp; Continue&apos;, you agree to Version 2.3.
          </p>
        </div>

        {/* Scrollable Terms Container */}
        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${
              isDarkMode ? "text-zinc-400" : "text-slate-500"
            }`}>
              Direct Licensing Agreement (v2.3)
            </span>
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[10px] font-bold text-[#ff00cc] hover:underline"
            >
              Open in new tab ↗
            </a>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`h-60 overflow-y-scroll p-5 text-[11px] leading-relaxed rounded-2xl border scrollbar-thin shadow-inner transition-colors ${
              isDarkMode 
                ? "bg-zinc-950 border-zinc-800 text-zinc-100" 
                : "bg-slate-50 border-slate-200 text-slate-800"
            }`}
          >
            <TermsOfService202611 />
            <div className={`py-4 text-center font-black tracking-widest text-[9px] uppercase ${
              isDarkMode ? "text-zinc-500" : "text-slate-400"
            }`}>
              --- End of Agreement ---
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleAccept}
            disabled={isSubmitting || !hasScrolledToBottom}
            className={`w-full py-4 text-xs font-black uppercase tracking-widest rounded-xl shadow-xl transition-all active:scale-98 disabled:opacity-30 disabled:pointer-events-none ${
              isDarkMode 
                ? "bg-[#ff00cc] hover:brightness-110 text-white" 
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isSubmitting 
              ? "Saving Agreement..." 
              : !hasScrolledToBottom 
                ? "Scroll to bottom to accept" 
                : "Accept & Continue"}
          </button>

          {!hasScrolledToBottom && (
            <p className={`text-[10px] text-center font-semibold ${
              isDarkMode ? "text-zinc-500" : "text-slate-400"
            }`}>
              Please review the terms above by scrolling to enable the accept button.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsUpdateModal;